/**
 * ActivityTrackingService - Service for tracking user activities and composition events
 * Handles event logging with debouncing and offline queue support
 */

import { APIService, APIServiceError } from '../api/APIService';
import { UserProfileError, UserProfileErrorType } from '../../types/user';

/**
 * Activity event types
 */
export type ActivityEventType =
  | 'ai_generate'
  | 'manual_compose'
  | 'composition_edit'
  | 'composition_share';

/**
 * Activity tracking request interface
 */
interface TrackActivityRequest {
  eventType: ActivityEventType;
  metadata?: Record<string, any>;
}

/**
 * Activity tracking response interface
 */
interface TrackActivityResponse {
  success: boolean;
  metrics?: {
    aiGenerateCount: number;
    manualComposeCount: number;
    compositionEditCount: number;
    compositionShareCount: number;
  };
}

/**
 * Queued event interface for offline support
 */
interface QueuedEvent {
  eventType: ActivityEventType;
  metadata?: Record<string, any>;
  timestamp: number;
  retries: number;
}

/**
 * API endpoint for activity tracking
 */
const USER_PROFILE_API_URL =
  import.meta.env['VITE_API_USER_PROFILE_URL'] ||
  'https://xyeqvofyij.execute-api.us-west-2.amazonaws.com/prod/users';

/**
 * ActivityTrackingService class for logging user activities
 * Extends APIService to inherit retry logic and error handling
 */
export class ActivityTrackingService extends APIService {
  private eventQueue: QueuedEvent[] = [];
  private debounceTimers: Map<string, NodeJS.Timeout> = new Map();
  private processingQueue = false;
  private readonly DEBOUNCE_DELAY = 2000; // 2 seconds
  private readonly MAX_RETRIES = 3;
  private readonly MAX_QUEUE_SIZE = 50;

  constructor() {
    super();
    this.loadQueueFromStorage();
    this.startQueueProcessor();
  }

  /**
   * Track AI composition generation event
   * 
   * @param metadata - Optional event metadata
   */
  async trackAIGeneration(metadata?: Record<string, any>): Promise<void> {
    await this.trackEvent('ai_generate', metadata);
  }

  /**
   * Track manual composition creation event
   * 
   * @param metadata - Optional event metadata
   */
  async trackManualCompose(metadata?: Record<string, any>): Promise<void> {
    await this.trackEvent('manual_compose', metadata);
  }

  /**
   * Track composition edit event
   * 
   * @param metadata - Optional event metadata
   */
  async trackCompositionEdit(metadata?: Record<string, any>): Promise<void> {
    await this.trackEventDebounced('composition_edit', metadata);
  }

  /**
   * Track composition share event
   * 
   * @param metadata - Optional event metadata
   */
  async trackCompositionShare(metadata?: Record<string, any>): Promise<void> {
    await this.trackEvent('composition_share', metadata);
  }

  /**
   * Track custom event
   * 
   * @param eventType - Event type identifier
   * @param metadata - Optional event metadata
   */
  async trackCustomEvent(
    eventType: ActivityEventType,
    metadata?: Record<string, any>
  ): Promise<void> {
    await this.trackEvent(eventType, metadata);
  }

  /**
   * Track event with debouncing to prevent duplicate events
   * 
   * @param eventType - Event type
   * @param metadata - Optional event metadata
   */
  private async trackEventDebounced(
    eventType: ActivityEventType,
    metadata?: Record<string, any>
  ): Promise<void> {
    // Clear existing timer for this event type
    const existingTimer = this.debounceTimers.get(eventType);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Set new timer
    const timer = setTimeout(() => {
      this.trackEvent(eventType, metadata);
      this.debounceTimers.delete(eventType);
    }, this.DEBOUNCE_DELAY);

    this.debounceTimers.set(eventType, timer);
  }

  /**
   * Track event immediately or add to queue if offline
   * 
   * @param eventType - Event type
   * @param metadata - Optional event metadata
   */
  private async trackEvent(
    eventType: ActivityEventType,
    metadata?: Record<string, any>
  ): Promise<void> {
    // Check if user is authenticated
    if (!this.isAuthenticated()) {
      console.warn('ActivityTrackingService: User not authenticated, skipping event tracking');
      return;
    }

    try {
      await this.sendTrackingRequest(eventType, metadata);
    } catch (error) {
      // Add to offline queue on failure
      console.warn('ActivityTrackingService: Failed to track event, adding to queue', error);
      this.addToQueue(eventType, metadata);
    }
  }

  /**
   * Send tracking request to API
   * 
   * @param eventType - Event type
   * @param metadata - Optional event metadata
   */
  private async sendTrackingRequest(
    eventType: ActivityEventType,
    metadata?: Record<string, any>
  ): Promise<TrackActivityResponse> {
    const payload: TrackActivityRequest = {
      eventType,
      ...(metadata && { metadata }),
    };

    try {
      const response = await this.fetchWithRetry(`${USER_PROFILE_API_URL}/activity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
        body: JSON.stringify(payload),
        retries: 2,
        retryDelay: 1000,
        timeout: 10000,
      });

      const data: TrackActivityResponse = await response.json();
      return data;
    } catch (error) {
      // Transform error but don't throw - tracking failures should be silent
      if (error instanceof APIServiceError) {
        throw new UserProfileError(
          UserProfileErrorType.NETWORK_ERROR,
          'Failed to track activity',
          error.status,
          error
        );
      }
      throw error;
    }
  }

  /**
   * Add event to offline queue
   * 
   * @param eventType - Event type
   * @param metadata - Optional event metadata
   */
  private addToQueue(eventType: ActivityEventType, metadata?: Record<string, any>): void {
    // Prevent queue from growing too large
    if (this.eventQueue.length >= this.MAX_QUEUE_SIZE) {
      console.warn('ActivityTrackingService: Queue full, dropping oldest event');
      this.eventQueue.shift();
    }

    const queuedEvent: QueuedEvent = {
      eventType,
      timestamp: Date.now(),
      retries: 0,
    };

    if (metadata) {
      queuedEvent.metadata = metadata;
    }

    this.eventQueue.push(queuedEvent);
    this.saveQueueToStorage();
  }

  /**
   * Process queued events
   */
  private async processQueue(): Promise<void> {
    if (this.processingQueue || this.eventQueue.length === 0) {
      return;
    }

    if (!this.isAuthenticated()) {
      return;
    }

    this.processingQueue = true;

    const eventsToProcess = [...this.eventQueue];
    this.eventQueue = [];

    for (const event of eventsToProcess) {
      try {
        await this.sendTrackingRequest(event.eventType, event.metadata);
      } catch (error) {
        // Re-queue if under retry limit
        if (event.retries < this.MAX_RETRIES) {
          event.retries++;
          this.eventQueue.push(event);
        } else {
          console.warn(
            'ActivityTrackingService: Max retries reached for event, dropping',
            event
          );
        }
      }
    }

    this.saveQueueToStorage();
    this.processingQueue = false;
  }

  /**
   * Start background queue processor
   */
  private startQueueProcessor(): void {
    // Process queue every 30 seconds
    setInterval(() => {
      this.processQueue();
    }, 30000);

    // Also process on visibility change (when user returns to tab)
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          this.processQueue();
        }
      });
    }

    // Process on online event
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.processQueue();
      });
    }
  }

  /**
   * Save queue to localStorage
   */
  private saveQueueToStorage(): void {
    try {
      localStorage.setItem('activityQueue', JSON.stringify(this.eventQueue));
    } catch (error) {
      console.warn('ActivityTrackingService: Failed to save queue to storage', error);
    }
  }

  /**
   * Load queue from localStorage
   */
  private loadQueueFromStorage(): void {
    try {
      const stored = localStorage.getItem('activityQueue');
      if (stored) {
        this.eventQueue = JSON.parse(stored);
      }
    } catch (error) {
      console.warn('ActivityTrackingService: Failed to load queue from storage', error);
      this.eventQueue = [];
    }
  }

  /**
   * Clear the event queue (for testing or manual cleanup)
   */
  public clearQueue(): void {
    this.eventQueue = [];
    this.saveQueueToStorage();
  }

  /**
   * Get current queue size (for monitoring)
   */
  public getQueueSize(): number {
    return this.eventQueue.length;
  }
}

// Export singleton instance
export const activityTrackingService = new ActivityTrackingService();
